// @givmo/fundraisers-module — the season setup's reads (FCM-WEB34): a campaign's pledge template (GFU-BE64),
// the current Terms of Use the setup shows (D-FCM-65), and the server's own sentence for a pledge's terms
// (GFU-BE93's preview, which writes nothing).
import type { UseQueryOptions } from '@tanstack/react-query';
import {
  isGivmoError,
  sdk,
  type PledgeOfferCreate,
  type PledgeRuleSentencePreviewOut,
  type PledgeTemplateOut,
} from '@givmo/api-client';
import {
  pledgesGetPledgeTemplateQueryKey,
  termsGetLatestTermsQueryKey,
} from '@givmo/api-client/query';
import { currentTerms, type CurrentTerms } from './season-setup-decode';

/** A campaign's pledge template, or null where it carries none (the read's 404). */
export function pledgeTemplateOptions(
  fundraiserId: string,
): UseQueryOptions<PledgeTemplateOut | null, Error> {
  const options = { path: { fundraiser_id: fundraiserId } };
  return {
    queryKey: pledgesGetPledgeTemplateQueryKey(options),
    queryFn: async ({ signal }) => {
      try {
        const { data } = await sdk.pledgesGetPledgeTemplate({
          ...options,
          signal,
          throwOnError: true,
        });
        return data ?? null;
      } catch (error) {
        if (isGivmoError(error) && error.status === 404) return null;
        throw error;
      }
    },
  };
}

/** The Terms of Use in force, which an organizer agrees to by setting up a season (D-FCM-65). */
export function currentTermsOptions(): UseQueryOptions<CurrentTerms, Error> {
  const options = { path: { document_type: 'terms_of_service' } };
  return {
    queryKey: termsGetLatestTermsQueryKey(options),
    queryFn: async ({ signal }) => {
      const { data } = await sdk.termsGetLatestTerms({ ...options, signal, throwOnError: true });
      const terms = currentTerms(data);
      if (!terms) throw new Error('The Terms of Use could not be read.');
      return terms;
    },
  };
}

/** "Pledgors will see": the server's sentence for these terms, composed by the one composer every read
 * and pledge uses; it screens the words as a create would, and writes nothing. */
export function rulePreviewOptions(
  body: PledgeOfferCreate | null,
): UseQueryOptions<PledgeRuleSentencePreviewOut, Error> {
  return {
    queryKey: ['fundraisers-module', 'rule-preview', body],
    queryFn: async ({ signal }) => {
      const { data } = await sdk.pledgesPreviewPledgeOfferRule({
        body: body!,
        signal,
        throwOnError: true,
      });
      return data;
    },
    enabled: body !== null,
    retry: false,
  };
}
